import axios from 'axios';

import ApiClient from './ApiClient';

export class WhatsappAPI extends ApiClient {
  constructor() {
    super('templates', { accountScoped: true, apiVersion: 'v3' });
  }

  // eslint-disable-next-line class-methods-use-this
  extractDataFromResponse(response) {
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  marshallData(dataToParse) {
    return { data: dataToParse };
  }

  get = async (headers, accountId) => {
    try {
      const response = await axios.get(
        `/api/v3/whatsapp/templates/whatsapp_inbox?account_id=${accountId}`,
        {
          headers: headers,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to make GET request: ${error.message}`);
    }
  };

  async getInbox({ accountId, inboxId, token }) {
    const headers = {};
    // Only add api_access_token header if token is provided (like other APIs)
    if (token) {
      headers.api_access_token = token;
    }
    try {
      const data = await this.get(headers, accountId);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  }

  post = async (accountId, headers, data) => {
    try {
      const url = `/api/v3/whatsapp/templates/create_templates?account_id=${accountId}`;
      const response = await axios.post(url, data, { headers });
      return response;
    } catch (error) {
      throw this.templateRequestError(error);
    }
  };

  templateRequestError(error) {
    const apiError = error?.response?.data?.error;
    let message = 'Could not create the WhatsApp template.';
    if (typeof apiError === 'string' && apiError.trim()) {
      message = apiError;
    } else if (apiError?.error_user_msg) {
      message = apiError.error_user_msg;
    } else if (typeof apiError?.message === 'string' && apiError.message.trim()) {
      message = apiError.message;
    }
    const wrapped = new Error(message);
    wrapped.response = error?.response;
    return wrapped;
  }

  async createTemplate({ accountId, data }) {
    const headers = {
      'api-access-token' : data.user.access_token,
    }
    try {
      return this.post(accountId, headers, data);
    } catch (error) {
      throw new Error('Failed to create template:', error);
    }
  }

  getTemp = async headers => {
    try {
      const response = await axios.get(
        `/api/v3/whatsapp/templates?account_id=1`,
        {
          headers: headers,
        }
      );
      return this.extractDataFromResponse(response);
    } catch (error) {
      throw new Error(`Failed to make GET request: ${error.message}`);
    }
  };

  getTemplates({ accountId }) {
    const headers = {
      Authorization: `Bearer ${''}`,
      api_access_token: '',
    };
    return this.getTemp(headers, accountId);
  };

  async fetchInboxchannelApi({ inboxId, access_token, accountId }) {
    try {
      const response = await axios.get(
        `/api/v1/accounts/${accountId}/inboxes/${inboxId}`,
        {
          headers: {
            'api-access-token' : access_token,
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (err) {
      console.error("Error While Fetching the Data", err);
    }
  };

  // Fetch templates from local database (for display with soft delete support)
  // Supports pagination with page and perPage params
  async fetchTemplates({ inbox, accountId, access_token, page = 1, perPage = 15, status = '', search = '' }) {
    try {
      const inboxId = inbox?.data?.id || inbox?.id;
      let url = `/api/v3/whatsapp/templates/local/${inboxId}/list?account_id=${accountId}&page=${page}&per_page=${perPage}`;
      if (status) {
        url += `&status=${encodeURIComponent(status)}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const response = await axios.get(url, {
        headers: {
          'api-access-token': access_token,
          'Content-Type': 'application/json',
        },
      });
      return response;
    } catch (err) {
      console.error("Error While Fetching the Data", err);
      throw err;
    }
  }

  // Fetch templates via backend API (proxy to WhatsApp Graph API) - use for syncing
  async fetchTemplatesFromGraph({ inbox, accountId, access_token }) {
    try {
      const inboxId = inbox?.data?.id || inbox?.id;
      const response = await axios.get(
        `/api/v3/whatsapp/templates/graph/${inboxId}/fetch?account_id=${accountId}`,
        {
          headers: {
            'api-access-token': access_token,
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (err) {
      console.error("Error While Fetching the Data from Graph API", err);
      throw err;
    }
  }

  // View a single template via backend API (proxy to WhatsApp Graph API)
  async ViewTemplates({ inboxid, inboxId, accountId, access_token }) {
    try {
      const templateId = inboxid; // inboxid is actually the template ID in original code
      const response = await axios.get(
        `/api/v3/whatsapp/templates/graph/${inboxId}/view/${templateId}?account_id=${accountId}`,
        {
          headers: {
            'api-access-token': access_token,
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (err) {
      console.error("Error While Fetching the Data", err);
      throw err;
    }
  }

  // Delete template via backend API (proxy to WhatsApp Graph API + soft delete locally)
  async deleteTemplates({ name, inbox, accountId, access_token }) {
    try {
      const inboxId = inbox?.data?.id || inbox?.id;
      const response = await axios.delete(
        `/api/v3/whatsapp/templates/graph/${inboxId}/delete?account_id=${accountId}&name=${encodeURIComponent(name)}`,
        {
          headers: {
            'api-access-token': access_token,
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (error) {
      console.error("Error while deleting the Template", error);
      throw error;
    }
  }

  // Check if template is being used by any macros/campaigns
  async checkTemplateUsage({ channelId, templateId, accountId, access_token }) {
    try {
      const response = await axios.get(
        `/api/v3/whatsapp/templates/${channelId}/${templateId}/usage?account_id=${accountId}`,
        {
          headers: {
            'api-access-token': access_token,
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (error) {
      console.error("Error checking template usage", error);
      throw error;
    }
  }

  // Soft delete template from CRM (local database only, does NOT delete from WhatsApp)
  async softDeleteTemplate({ channelId, templateId, accountId, access_token }) {
    try {
      const response = await axios.delete(
        `/api/v3/whatsapp/templates/${channelId}/${templateId}?account_id=${accountId}`,
        {
          headers: {
            'api-access-token': access_token,
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (error) {
      console.error("Error while soft deleting the Template from CRM", error);
      throw error;
    }
  }

  // Restore a soft-deleted template
  async restoreTemplate({ channelId, templateId, accountId, access_token }) {
    try {
      const response = await axios.post(
        `/api/v3/whatsapp/templates/${channelId}/${templateId}/restore?account_id=${accountId}`,
        {},
        {
          headers: {
            'api-access-token': access_token,
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (error) {
      console.error("Error while restoring the Template", error);
      throw error;
    }
  }

  // Edit template via backend API (proxy to WhatsApp Graph API)
  async submitEditData({ inboxId, templateId, dataToSend, accountId, access_token }) {
    try {
      const response = await axios.post(
        `/api/v3/whatsapp/templates/graph/${inboxId}/edit/${templateId}?account_id=${accountId}`,
        dataToSend,
        {
          headers: {
            'api-access-token': access_token,
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (err) {
      if (err.response) {
        console.error('Error response data:', err.response.data);
        return { success: false, error: err.response.data };
      } else if (err.request) {
        console.error('Error request data:', err.request);
        return { success: false, error: 'No response received from the server' };
      } else {
        console.error('Error message:', err.message);
        return { success: false, error: err.message };
      }
    }
  }
}
export default new WhatsappAPI();
